import type { RouteObject } from 'react-router';

import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiOrgId,
  ApiVolopId,
} from 'api/types/ApiTypedId';
import type { LayoutHandleProps } from 'layouts/Layout/LayoutHandleProps';
import { listingApiFetchRsvps } from 'modules/listing/api/event/listingApiFetchRsvps';
import { orgDashboardApiFetchEvent } from 'modules/orgDashboard/api/listing/orgDashboardApiFetchEvent';
import { orgDashboardApiFetchInternship } from 'modules/orgDashboard/api/listing/orgDashboardApiFetchInternship';
import { orgDashboardApiFetchJob } from 'modules/orgDashboard/api/listing/orgDashboardApiFetchJob';
import { orgDashboardApiFetchVolop } from 'modules/orgDashboard/api/listing/orgDashboardApiFetchVolop';
import { orgDashboardApiFetchListingApplications } from 'modules/orgDashboard/api/listingApplication/orgDashboardApiFetchListingApplications';
import { orgDashboardApiFetchRecommendedCandidates } from 'modules/orgDashboard/api/suggestedApplicants/orgDashboardApiFetchRecommendedCandidates';
import { OrgDashboardPreviewEventPageLoadable } from 'modules/orgDashboard/pages/events/preview/OrgDashboardPreviewEventPageLoadable';
import { OrgDashboardViewEventPageLoadable } from 'modules/orgDashboard/pages/events/view/OrgDashboardViewEventPageLoadable';
import { OrgDashboardPreviewInternshipPageLoadable } from 'modules/orgDashboard/pages/internships/preview/OrgDashboardPreviewInternshipPageLoadable';
import { OrgDashboardViewInternshipPageLoadable } from 'modules/orgDashboard/pages/internships/view/OrgDashboardViewInternshipPageLoadable';
import { OrgDashboardPreviewJobPageLoadable } from 'modules/orgDashboard/pages/jobs/preview/OrgDashboardPreviewJobPageLoadable';
import { OrgDashboardViewJobPageLoadable } from 'modules/orgDashboard/pages/jobs/view/OrgDashboardViewJobPageLoadable';
import { OrgDashboardViewListingApplicationPageContainerLoadable } from 'modules/orgDashboard/pages/listings/viewApplication/OrgDashboardViewListingApplicationPageContainerLoadable';
import { OrgDashboardPreviewOrgPageContainerLoadable } from 'modules/orgDashboard/pages/orgs/preview/OrgDashboardPreviewOrgPageContainerLoadable';
import { OrgDashboardViewJobRecommendedCandidatePageLoadable } from 'modules/orgDashboard/pages/recommendedCandidates/view/OrgDashboardViewJobRecommendedCandidatePageLoadable';
import { OrgDashboardPreviewVolopPageLoadable } from 'modules/orgDashboard/pages/volops/preview/OrgDashboardPreviewVolopPageLoadable';
import { OrgDashboardViewVolopPageLoadable } from 'modules/orgDashboard/pages/volops/view/OrgDashboardViewVolopPageLoadable';
import { NotFoundPageLoadable } from 'pages/NotFound/NotFoundPageLoadable';
import type { AppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import { loadableLoader } from 'routing/utils/loadableLoader';
import { renderLoader } from 'routing/utils/renderLoader/renderLoader';

import {
  orgDashboardPreviewEventRoute,
  orgDashboardPreviewInternshipRoute,
  orgDashboardPreviewJobRoute,
  orgDashboardPreviewOrgRoute,
  orgDashboardPreviewVolopRoute,
  orgDashboardViewApplicationRoute,
  orgDashboardViewEventRoute,
  orgDashboardViewInternshipRoute,
  orgDashboardViewJobRecommendedCandidateRoute,
  orgDashboardViewJobRoute,
  orgDashboardViewVolopRoute,
} from './orgDashboard.routes';

export const noSubLayoutOrgDashboardRouteObjects = [
  {
    path: orgDashboardPreviewOrgRoute.relativePath,
    loader: loadableLoader(OrgDashboardPreviewOrgPageContainerLoadable),
    Component: OrgDashboardPreviewOrgPageContainerLoadable,
  },
  {
    path: orgDashboardViewJobRoute.relativePath,
    shouldRevalidate: ({ currentParams, nextParams }) =>
      currentParams.jobId !== nextParams.jobId,
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardViewJobRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const jobId = params.jobId as ApiJobId;

        const [job, listingApplications, recommendedCandidates] =
          await Promise.all([
            orgDashboardApiFetchJob(orgId, jobId).catch(() => null),
            orgDashboardApiFetchListingApplications(orgId, {
              type: 'JOB',
              id: jobId,
            }),
            orgDashboardApiFetchRecommendedCandidates(orgId, jobId).catch(
              () => null,
            ),
            OrgDashboardViewJobPageLoadable.load(),
          ]);

        if (!job) await NotFoundPageLoadable.load();

        return { job, listingApplications, recommendedCandidates };
      },
      render: ({ job, listingApplications, recommendedCandidates }) =>
        job ? (
          <OrgDashboardViewJobPageLoadable
            key={job.id}
            job={job}
            listingApplications={listingApplications}
            recommendedCandidates={recommendedCandidates || []}
          />
        ) : (
          <NotFoundPageLoadable noLayout />
        ),
    }),
  },
  {
    path: orgDashboardViewInternshipRoute.relativePath,
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardViewInternshipRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const internshipId = params.internshipId as ApiInternshipId;

        const [internship, listingApplications] = await Promise.all([
          orgDashboardApiFetchInternship(orgId, internshipId).catch(() => null),
          orgDashboardApiFetchListingApplications(orgId, {
            type: 'INTERNSHIP',
            id: internshipId,
          }),
          OrgDashboardViewInternshipPageLoadable.load(),
        ]);

        if (!internship) await NotFoundPageLoadable.load();

        return { internship, listingApplications };
      },
      render: ({ internship, listingApplications }) =>
        internship ? (
          <OrgDashboardViewInternshipPageLoadable
            key={internship.id}
            internship={internship}
            listingApplications={listingApplications}
          />
        ) : (
          <NotFoundPageLoadable noLayout />
        ),
    }),
  },
  {
    path: orgDashboardViewVolopRoute.relativePath,
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardViewVolopRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const volopId = params.volopId as ApiVolopId;

        const [volop, listingApplications] = await Promise.all([
          orgDashboardApiFetchVolop(orgId, volopId).catch(() => null),
          orgDashboardApiFetchListingApplications(orgId, {
            type: 'VOLOP',
            id: volopId,
          }),
          OrgDashboardViewVolopPageLoadable.load(),
        ]);

        if (!volop) await NotFoundPageLoadable.load();

        return { volop, listingApplications };
      },
      render: ({ volop, listingApplications }) =>
        volop ? (
          <OrgDashboardViewVolopPageLoadable
            key={volop.id}
            volop={volop}
            listingApplications={listingApplications}
          />
        ) : (
          <NotFoundPageLoadable noLayout />
        ),
    }),
  },
  {
    path: orgDashboardViewEventRoute.relativePath,
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardViewEventRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const eventId = params.eventId as ApiEventId;

        const [event, rsvps] = await Promise.all([
          orgDashboardApiFetchEvent(orgId, eventId).catch(() => null),
          listingApiFetchRsvps(eventId).catch(() => []),
          OrgDashboardViewEventPageLoadable.load(),
        ]);

        if (!event) await NotFoundPageLoadable.load();

        return { event, rsvps };
      },
      render: ({ event, rsvps }) =>
        event ? (
          <OrgDashboardViewEventPageLoadable
            key={event.id}
            event={event}
            rsvps={rsvps}
          />
        ) : (
          <NotFoundPageLoadable noLayout />
        ),
    }),
  },
  {
    path: orgDashboardViewApplicationRoute.relativePath,
    loader: loadableLoader(
      OrgDashboardViewListingApplicationPageContainerLoadable,
    ),
    Component: OrgDashboardViewListingApplicationPageContainerLoadable,
  },
  {
    path: orgDashboardViewJobRecommendedCandidateRoute.relativePath,
    handle: {
      fallbackUrlByLocaleToCurrent: false,
    } satisfies LayoutHandleProps,
    loader: loadableLoader(OrgDashboardViewJobRecommendedCandidatePageLoadable),
    Component: OrgDashboardViewJobRecommendedCandidatePageLoadable,
  },
  // Preview listing routes
  {
    path: orgDashboardPreviewJobRoute.relativePath,
    handle: {
      darkBackground: false,
    },
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardPreviewJobRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const jobId = params.jobId as ApiJobId;

        const [job] = await Promise.all([
          orgDashboardApiFetchJob(orgId, jobId),
          OrgDashboardPreviewJobPageLoadable.load(),
        ]);

        return job;
      },
      render: (job) => (
        <OrgDashboardPreviewJobPageLoadable key={job.id} job={job} />
      ),
    }),
  },
  {
    path: orgDashboardPreviewInternshipRoute.relativePath,
    handle: {
      darkBackground: false,
    },
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardPreviewInternshipRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const internshipId = params.internshipId as ApiInternshipId;

        const [internship] = await Promise.all([
          orgDashboardApiFetchInternship(orgId, internshipId),
          OrgDashboardPreviewInternshipPageLoadable.load(),
        ]);

        return internship;
      },
      render: (internship) => (
        <OrgDashboardPreviewInternshipPageLoadable
          key={internship.id}
          internship={internship}
        />
      ),
    }),
  },
  {
    path: orgDashboardPreviewVolopRoute.relativePath,
    handle: {
      darkBackground: false,
    },
    ...renderLoader({
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardPreviewVolopRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const volopId = params.volopId as ApiVolopId;

        const [volop] = await Promise.all([
          orgDashboardApiFetchVolop(orgId, volopId),
          OrgDashboardPreviewVolopPageLoadable.load(),
        ]);

        return volop;
      },
      render: (volop) => (
        <OrgDashboardPreviewVolopPageLoadable key={volop.id} volop={volop} />
      ),
    }),
  },
  {
    path: orgDashboardPreviewEventRoute.relativePath,
    handle: {
      darkBackground: false,
    },
    ...renderLoader({
      clientOnly: true,
      loader: async ({
        params,
      }: AppRouteLoaderArgs<typeof orgDashboardPreviewEventRoute>) => {
        const orgId = params.orgId as ApiOrgId;
        const eventId = params.eventId as ApiEventId;

        const [event] = await Promise.all([
          orgDashboardApiFetchEvent(orgId, eventId),
          OrgDashboardPreviewEventPageLoadable.load(),
        ]);

        return event;
      },
      render: (event) => (
        <OrgDashboardPreviewEventPageLoadable key={event.id} event={event} />
      ),
    }),
  },
] satisfies RouteObject[];
